import { useEffect, useRef, useState, useCallback } from "react";
import { BarChart3, Eraser, Pencil, Clock, Plus, Send, X, ShoppingCart, Play, Trash2, Download, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const voterId = (() => {
  let v = localStorage.getItem("vitalis_voter");
  if (!v) { v = Math.random().toString(36).slice(2); localStorage.setItem("vitalis_voter", v); }
  return v;
})();

const COLORS = ["#0A2540", "#0284C7", "#DC2626", "#16A34A", "#F59E0B"];
const REACTIONS = ["👏", "❤️", "👍", "😮", "🎉"];
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export function SessionTools({ token, aid, isHost = false, bindExport }) {
  const [poll, setPoll] = useState(null);
  const [time, setTime] = useState(null);
  const [voted, setVoted] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [newOpts, setNewOpts] = useState(["", ""]);
  const [newDur, setNewDur] = useState(0);
  const [prepared, setPrepared] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [color, setColor] = useState(COLORS[0]);
  const canvasRef = useRef(null);
  const versionRef = useRef(0);
  const strokesRef = useRef([]);
  const drawingRef = useRef(null);

  const redraw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    for (const s of strokesRef.current) {
      if (!s.points || s.points.length < 1) continue;
      ctx.strokeStyle = s.color || "#0A2540"; ctx.lineWidth = s.width || 3;
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath(); ctx.moveTo(s.points[0][0], s.points[0][1]);
      for (const p of s.points.slice(1)) ctx.lineTo(p[0], p[1]);
      ctx.stroke();
    }
  }, []);

  const poll_ = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get(`/visio/session/${token}/state?wb_since=${versionRef.current}`);
      setPoll(data.poll); setTime(data.time); setReactions(data.reactions || []);
      const wb = data.whiteboard || {};
      if ((wb.strokes || []).length) {
        strokesRef.current = strokesRef.current.concat(wb.strokes);
        versionRef.current = wb.version;
        redraw();
      } else if (wb.version < versionRef.current) {
        strokesRef.current = []; versionRef.current = 0; redraw(); // board cleared
      }
      if (isHost && aid) {
        try { const r = await api.get(`/admin/visio/appointments/${aid}/polls`); setPrepared(r.data.filter((p) => p.status === "prepared")); } catch { /* */ }
      }
    } catch { /* silencieux */ }
  }, [token, redraw, isHost, aid]);

  useEffect(() => { poll_(); const t = setInterval(poll_, 2500); return () => clearInterval(t); }, [poll_]);
  useEffect(() => { setVoted(false); }, [poll?.id]);

  const vote = async (i) => {
    try {
      await api.post(`/visio/session/${token}/poll/vote`, { poll_id: poll.id, option_index: i, voter_id: voterId });
      setVoted(true); poll_();
    } catch (e) { if (e?.response?.status === 409) setVoted(true); else toast.error(apiError(e)); }
  };
  const launchPoll = async () => {
    const opts = newOpts.map((o) => o.trim()).filter(Boolean);
    if (!newQ.trim() || opts.length < 2) { toast.error("Question + 2 options minimum"); return; }
    try { await api.post(`/admin/visio/appointments/${aid}/poll`, { question: newQ, options: opts, duration_sec: Number(newDur) || 0 }); setNewQ(""); setNewOpts(["", ""]); setNewDur(0); poll_(); toast.success("Sondage lancé"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const preparePoll = async () => {
    const opts = newOpts.map((o) => o.trim()).filter(Boolean);
    if (!newQ.trim() || opts.length < 2) { toast.error("Question + 2 options minimum"); return; }
    try { await api.post(`/admin/visio/appointments/${aid}/poll/prepare`, { question: newQ, options: opts, duration_sec: Number(newDur) || 0 }); setNewQ(""); setNewOpts(["", ""]); setNewDur(0); poll_(); toast.success("Sondage préparé"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const launchPrepared = async (pid) => { try { await api.post(`/admin/visio/appointments/${aid}/poll/${pid}/launch`); poll_(); toast.success("Sondage lancé"); } catch (e) { toast.error(apiError(e)); } };
  const deletePrepared = async (pid) => { try { await api.delete(`/admin/visio/appointments/${aid}/poll/${pid}`); poll_(); } catch (e) { toast.error(apiError(e)); } };
  const sendReaction = (emoji) => { api.post(`/visio/session/${token}/reaction`, { emoji }).catch(() => {}); setReactions((r) => [...r, { emoji, at: Date.now() + "" }]); };
  const exportCsv = () => { window.open(`${BACKEND}/api/admin/visio/appointments/${aid}/polls/export`, "_blank"); };
  const exportPollPng = () => {
    if (!poll) return;
    const w = 520, rowH = 34, top = 70;
    const cv = document.createElement("canvas"); cv.width = w; cv.height = top + poll.options.length * rowH + 20;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = "#0A2540"; ctx.font = "bold 18px sans-serif"; ctx.fillText(poll.question, 20, 36);
    ctx.font = "13px sans-serif"; ctx.fillText(`${poll.total} vote(s)`, 20, 56);
    poll.options.forEach((o, i) => {
      const y = top + i * rowH; const pct = poll.total ? Math.round((o.votes / poll.total) * 100) : 0;
      ctx.fillStyle = "#E2E8F0"; ctx.fillRect(20, y, w - 40, 22);
      ctx.fillStyle = "#0284C7"; ctx.fillRect(20, y, (w - 40) * pct / 100, 22);
      ctx.fillStyle = "#0A2540"; ctx.font = "12px sans-serif"; ctx.fillText(`${o.text} — ${o.votes} (${pct}%)`, 26, y + 15);
    });
    const a = document.createElement("a"); a.href = cv.toDataURL("image/png"); a.download = `sondage-${new Date().toISOString().slice(0, 10)}.png`; a.click();
  };
  const closePoll = async () => { try { await api.post(`/admin/visio/appointments/${aid}/poll/close`); poll_(); } catch (e) { toast.error(apiError(e)); } };
  const clearBoard = async () => { try { await api.post(`/admin/visio/appointments/${aid}/whiteboard/clear`); strokesRef.current = []; versionRef.current = 0; redraw(); } catch (e) { toast.error(apiError(e)); } };
  const boardToPng = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return null;
    const out = document.createElement("canvas"); out.width = cv.width; out.height = cv.height;
    const ctx = out.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, out.width, out.height); ctx.drawImage(cv, 0, 0);
    return out.toDataURL("image/png");
  }, []);
  useEffect(() => { if (bindExport) bindExport(boardToPng); }, [bindExport, boardToPng]);
  const exportPng = () => {
    const url = boardToPng(); if (!url) return;
    const a = document.createElement("a"); a.href = url; a.download = `tableau-blanc-${new Date().toISOString().slice(0, 10)}.png`; a.click();
  };

  const pos = (e) => {
    const cv = canvasRef.current; const r = cv.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return [Math.round((cx - r.left) * (cv.width / r.width)), Math.round((cy - r.top) * (cv.height / r.height))];
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = { color, width: 3, points: [pos(e)], author: voterId }; };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); drawingRef.current.points.push(pos(e)); strokesRef.current = strokesRef.current.concat([]); redraw(); const s = drawingRef.current; const ctx = canvasRef.current.getContext("2d"); ctx.strokeStyle = s.color; ctx.lineWidth = s.width; ctx.lineCap = "round"; ctx.beginPath(); const pts = s.points; ctx.moveTo(pts[pts.length - 2]?.[0] ?? pts[0][0], pts[pts.length - 2]?.[1] ?? pts[0][1]); ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]); ctx.stroke(); };
  const end = async () => {
    const s = drawingRef.current; drawingRef.current = null;
    if (!s || s.points.length < 1) return;
    strokesRef.current = strokesRef.current.concat([s]); redraw();
    try { const { data } = await api.post(`/visio/session/${token}/whiteboard/stroke`, { stroke: s }); versionRef.current = data.version; } catch { /* */ }
  };

  const mmss = (iso) => {
    if (!iso) return "—";
    const diff = Math.max(0, Math.floor((new Date(iso) - new Date()) / 1000));
    return `${String(Math.floor(diff / 60)).padStart(2, "0")}:${String(diff % 60).padStart(2, "0")}`;
  };
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);

  return (
    <div className="space-y-4" data-testid="session-tools">
      {/* RÉACTIONS EN DIRECT */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="session-reactions">
        <div className="flex flex-wrap items-center gap-2">
          {REACTIONS.map((em) => (
            <button key={em} onClick={() => sendReaction(em)} data-testid={`reaction-${em}`} className="rounded-full border border-slate-200 px-2.5 py-1 text-lg transition-transform hover:scale-125 active:scale-95">{em}</button>
          ))}
          <div className="ml-auto flex min-h-[28px] items-end gap-0.5 overflow-hidden" data-testid="reaction-feed">
            {reactions.slice(-8).map((r, i) => (
              <span key={r.at + i} className="animate-bounce text-lg" style={{ animationDuration: "1s" }}>{r.emoji}</span>
            ))}
          </div>
        </div>
      </div>

      {/* TEMPS DE CONNEXION */}
      {time && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="session-time">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold text-navy"><Clock size={15} className="text-ciel" /> Temps de session</span>
            <span className="font-mono text-lg font-bold text-navy" data-testid="session-countdown" data-tick={tick}>{mmss(time.end)}</span>
          </div>
          {time.remaining_units != null && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Unités restantes</span>
                <span className="font-mono font-semibold text-navy" data-testid="session-units">{time.remaining_units}</span>
              </div>
              <a href="/offres" target="_blank" rel="noopener noreferrer" data-testid="session-buy-units"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-400">
                <ShoppingCart size={13} /> Acheter des unités de temps
              </a>
            </div>
          )}
        </div>
      )}

      {/* SONDAGE */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="session-poll">
        <p className="flex items-center gap-2 text-sm font-semibold text-navy"><BarChart3 size={15} className="text-ciel" /> Sondage en direct</p>
        {poll ? (
          <div className="mt-3">
            <p className="text-sm font-medium text-navy">{poll.question}</p>
            {poll.open && poll.remaining_sec != null && (
              <p className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-amber-600" data-testid="poll-timer"><Timer size={11} /> Fermeture dans {poll.remaining_sec}s</p>
            )}
            <div className="mt-2 space-y-1.5">
              {poll.options.map((o, i) => {
                const pct = poll.total ? Math.round((o.votes / poll.total) * 100) : 0;
                return (
                  <button key={i} disabled={!poll.open || voted || isHost} onClick={() => vote(i)} data-testid={`poll-option-${i}`}
                    className="relative w-full overflow-hidden rounded-lg border border-slate-200 px-3 py-1.5 text-left text-xs disabled:cursor-default enabled:hover:border-ciel">
                    <span className="absolute inset-y-0 left-0 bg-sky-100" style={{ width: `${pct}%` }} />
                    <span className="relative flex justify-between"><span className="text-navy">{o.text}</span><span className="font-mono text-slate-500">{o.votes} · {pct}%</span></span>
                  </button>
                );
              })}
            </div>
            {!poll.open && <p className="mt-2 text-[11px] text-slate-400">Sondage clos.</p>}
            {voted && poll.open && <p className="mt-2 text-[11px] text-emerald-600">Vote enregistré ✓</p>}
            {isHost && poll.open && <Button size="sm" variant="outline" onClick={closePoll} data-testid="poll-close" className="mt-3 rounded-full text-xs"><X size={12} className="mr-1" /> Clore le sondage</Button>}
            {isHost && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="ghost" onClick={exportPollPng} data-testid="poll-export-png" className="text-xs"><Download size={12} className="mr-1" /> PNG</Button>
                <Button size="sm" variant="ghost" onClick={exportCsv} data-testid="poll-export-csv" className="text-xs"><Download size={12} className="mr-1" /> CSV</Button>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-400">{isHost ? "Aucun sondage actif." : "En attente d'un sondage de l'organisateur."}</p>
        )}
        {isHost && prepared.length > 0 && (
          <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3" data-testid="poll-prepared-list">
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Sondages préparés ({prepared.length})</p>
            {prepared.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid={`poll-prepared-${p.id}`}>
                <span className="flex-1 truncate text-xs text-navy">{p.question}</span>
                <button onClick={() => launchPrepared(p.id)} data-testid={`poll-launch-prepared-${p.id}`} className="rounded-full bg-ciel px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700"><Play size={11} className="inline" /> Lancer</button>
                <button onClick={() => deletePrepared(p.id)} data-testid={`poll-del-prepared-${p.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
        {isHost && (!poll || !poll.open) && (
          <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" data-testid="poll-composer">
            <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Votre question…" className="text-xs" data-testid="poll-question" />
            {newOpts.map((o, i) => (
              <Input key={i} value={o} onChange={(e) => setNewOpts(newOpts.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Option ${i + 1}`} className="text-xs" data-testid={`poll-opt-${i}`} />
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setNewOpts([...newOpts, ""])} className="text-xs" data-testid="poll-add-opt"><Plus size={12} className="mr-1" /> Option</Button>
              <div className="flex items-center gap-1 text-xs text-slate-500"><Timer size={12} /><Input type="number" min={0} value={newDur} onChange={(e) => setNewDur(e.target.value)} placeholder="0" className="h-7 w-16 text-xs" data-testid="poll-duration" /><span>s</span></div>
              <Button size="sm" variant="outline" onClick={preparePoll} className="ml-auto rounded-full text-xs" data-testid="poll-prepare"><Plus size={12} className="mr-1" /> Préparer</Button>
              <Button size="sm" onClick={launchPoll} className="rounded-full bg-ciel text-xs text-white hover:bg-sky-700" data-testid="poll-launch"><Send size={12} className="mr-1" /> Lancer</Button>
            </div>
          </div>
        )}
      </div>

      {/* TABLEAU BLANC */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="session-whiteboard">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy"><Pencil size={15} className="text-ciel" /> Tableau blanc</p>
          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} data-testid={`wb-color-${c.replace('#', '')}`} className={`h-5 w-5 rounded-full border-2 ${color === c ? "border-navy" : "border-transparent"}`} style={{ background: c }} />
            ))}
            <button onClick={exportPng} data-testid="wb-export" title="Exporter en PNG" className="ml-1 rounded p-1 text-slate-400 hover:text-ciel"><Download size={15} /></button>
            {isHost && <button onClick={clearBoard} data-testid="wb-clear" className="rounded p-1 text-slate-400 hover:text-red-500"><Eraser size={15} /></button>}
          </div>
        </div>
        <canvas ref={canvasRef} width={520} height={280} data-testid="wb-canvas"
          className="mt-3 w-full touch-none rounded-lg border border-slate-200 bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        <p className="mt-1.5 text-[11px] text-slate-400">Dessinez à la souris ou au doigt — partagé avec tous les participants.</p>
      </div>
    </div>
  );
}
